import { AgGridReact } from '@mint-lib/ag-grid/react';
import { useCallback, useEffect, useMemo, useRef, type RefObject } from 'react';

import {
  ClientSideRowModelApiModule,
  DateFilterModule,
  ModuleRegistry,
  PaginationModule,
  PinnedRowModule,
  RenderApiModule,
  RowApiModule,
  RowSelectionModule,
  RowStyleModule,
  SelectionChangedEvent,
  TooltipModule,
  ValueGetterParams,
  type ColDef,
  type ICellRendererParams,
  type RowClassParams,
} from '@mint-lib/ag-grid/community';
import {
  CellSelectionModule,
  ClientSideRowModelModule,
  ColumnMenuModule,
  ContextMenuModule,
  LicenseManager,
  RowGroupingModule,
  RowSelectionOptions,
  SetFilterModule,
} from '@mint-lib/ag-grid/enterprise';
import { useTranslation } from '@mint-lib/i18n';
import { StaticDateSpan } from '@mint-lib/tw-components/patterns';
import { cn } from '@mint-lib/tw-utils';

import { dateFilterParams } from '../../../../helpers/ag-grid-helpers.js';
import {
  getNodeCreatedAt,
  getNodeCreator,
  getNodeName,
  getNodeRowId,
  isFolderNode,
  isPendingFolder,
  isUnprocessedFile,
} from '../../../../helpers/knowledge-base/fileSystemNode.js';
import { toUserName } from '../../../../helpers/knowledge-base/userName.js';
import type {
  FileSystemNode,
  FolderNode,
} from '../../../../types/fileSystem.js';
import type { RagTableType } from '../../../../types/rag.js';
import {
  ActionsCell,
  DescriptionCell,
  FileTypeCell,
} from '../../RagTable/cells/index.js';
import { KnowledgeBaseNoRowOverlay } from '../../RagTable/NoRowsOverlay.jsx';
import { FolderActionsCell } from '../FolderSection/FolderActionsCell.jsx';
import { CreatorCell } from './cells/CreatorCell.jsx';
import { NameCell } from './cells/NameCell.jsx';
import {
  asFileCellParams,
  FOLDER_DRAFT_ROW_ID,
  FOLDER_DRAFT_ROWS,
  foldersFirstComparator,
  isDraftRow,
} from './fileSystemGrid.js';

ModuleRegistry.registerModules([
  RowGroupingModule,
  ClientSideRowModelApiModule,
  ClientSideRowModelModule,
  RenderApiModule,
  RowApiModule,
  PaginationModule,
  PinnedRowModule,
  RowSelectionModule,
  RowStyleModule,
  ContextMenuModule,
  ColumnMenuModule,
  SetFilterModule,
  DateFilterModule,
  CellSelectionModule,
  TooltipModule,
]);

LicenseManager.setLicenseKey(import.meta.env.VITE_AG_GRID_LICENSE_KEY);

const FOLDER_CELL_PLACEHOLDER = '-';

/** The create-folder row pinned on top while the user names a new folder. */
type DraftProps = {
  isDrafting: boolean;
  onCommit: (name: string) => boolean;
  onCancel: () => void;
};

type RenameProps = {
  folderId: string | null;
  onStart: (id: string) => void;
  onCommit: (id: string, name: string) => boolean;
  onCancel: () => void;
};

type Props = {
  gridRef: RefObject<AgGridReact | null>;
  isBucketRoot: boolean;
  tableData: FileSystemNode[];
  isLoading?: boolean;
  openingFolderId?: string | null;
  onSelectNodes: (nodes: FileSystemNode[]) => void;
  onUnselectTableRows: () => void;
  onOpenFolder: (folder: FolderNode) => void;
  onOpenDeleteDialog: (rows: RagTableType[]) => void;
  onOpenDeleteFolder: (folder: FolderNode) => void;
  canManageFolder: (folder: FolderNode) => boolean;
  canMoveFolder: (folder: FolderNode) => boolean;
  canShareFolder: (folder: FolderNode) => boolean;
  onShareFolder: (folder: FolderNode) => void;
  validateName: (name: string, excludeId?: string) => boolean;
  draft: DraftProps;
  rename: RenameProps;
  onOpenMoveDialog: (nodes: FileSystemNode[]) => void;
};

const FileSystemTable = ({
  gridRef,
  isBucketRoot,
  tableData,
  isLoading = false,
  openingFolderId = null,
  onSelectNodes,
  onUnselectTableRows,
  onOpenFolder,
  onOpenDeleteDialog,
  onOpenDeleteFolder,
  canManageFolder,
  canMoveFolder,
  canShareFolder,
  onShareFolder,
  validateName,
  draft: { isDrafting, onCommit: onCommitDraft, onCancel: onCancelDraft },
  rename: {
    folderId: renamingFolderId,
    onStart: onStartRename,
    onCommit: onCommitRename,
    onCancel: onCancelRename,
  },
  onOpenMoveDialog,
}: Props) => {
  const { t } = useTranslation('@mint-lib/ai-chat');

  const columnDefs = useMemo<ColDef<FileSystemNode>[]>(
    () => [
      {
        headerName: t('Name'),
        // Flex shares the drawer width: text columns stretch, the narrow ones
        // keep the width their content needs.
        flex: 3,
        minWidth: 220,
        valueGetter: (p: ValueGetterParams<FileSystemNode>) =>
          p.data && getNodeName(p.data),
        filter: true,
        comparator: foldersFirstComparator,
        cellRenderer: (params: ICellRendererParams<FileSystemNode>) => (
          <NameCell
            {...params}
            onOpenFolder={onOpenFolder}
            onCommitDraft={onCommitDraft}
            onCancelDraft={onCancelDraft}
            renamingFolderId={renamingFolderId}
            openingFolderId={openingFolderId}
            onCommitRename={onCommitRename}
            onCancelRename={onCancelRename}
            validateName={validateName}
          />
        ),
        tooltip: ({ data }) => {
          if (!isUnprocessedFile(data)) return null;

          return data.status === 'pending'
            ? t('The file is being processed')
            : t('The file could not be processed');
        },
        // Typed through ColDef: the package re-exports no params type.
        suppressKeyboardEvent: ({ node }) =>
          Boolean(node.rowPinned) || node.data?.id === renamingFolderId,
      },
      {
        headerName: t('Description'),
        field: 'description',
        flex: 2,
        minWidth: 160,
        cellRenderer: (params: ICellRendererParams<FileSystemNode>) => {
          if (isDraftRow(params)) return null;
          if (isFolderNode(params.data))
            return params.data.description || FOLDER_CELL_PLACEHOLDER;

          return DescriptionCell(asFileCellParams(params));
        },
        comparator: foldersFirstComparator,
      },
      {
        headerName: t('Creation date'),
        colId: 'createdAt',
        flex: 1,
        minWidth: 150,
        valueGetter: (p: ValueGetterParams<FileSystemNode>) =>
          p.data && getNodeCreatedAt(p.data),
        cellRenderer: (params: ICellRendererParams<FileSystemNode>) => {
          if (isDraftRow(params)) return null;

          return params.value ? (
            <StaticDateSpan date={params.value} />
          ) : (
            FOLDER_CELL_PLACEHOLDER
          );
        },
        filter: 'agDateColumnFilter',
        filterParams: dateFilterParams,
        comparator: foldersFirstComparator,
      },
      {
        headerName: t('Created by'),
        colId: 'createdBy',
        // Buckets have no creator or type worth showing.
        hide: isBucketRoot,
        width: 140,
        valueGetter: (p: ValueGetterParams<FileSystemNode>) => {
          const creator = p.data && getNodeCreator(p.data);

          return creator ? toUserName(creator) : '';
        },
        cellRenderer: (params: ICellRendererParams<FileSystemNode>) =>
          isDraftRow(params) ? null : <CreatorCell node={params.data} />,
        filter: true,
        comparator: foldersFirstComparator,
      },
      {
        headerName: t('Type'),
        colId: 'fileType',
        hide: isBucketRoot,
        width: 120,
        valueGetter: (p: ValueGetterParams<FileSystemNode>) =>
          p.data && (isFolderNode(p.data) ? '' : p.data.fileType),
        cellRenderer: (params: ICellRendererParams<FileSystemNode>) =>
          isDraftRow(params) || isFolderNode(params.data)
            ? null
            : FileTypeCell(asFileCellParams(params)),
        cellStyle: {
          display: 'flex',
          alignItems: 'center',
        },
        filter: true,
        comparator: foldersFirstComparator,
      },
      {
        headerComponent: () => null,
        colId: 'actions',
        pinned: 'right',
        filter: false,
        sortable: false,
        resizable: false,
        width: 40,
        cellRenderer: (params: ICellRendererParams<FileSystemNode>) => {
          if (params.node?.rowPinned) return null;
          const node = params.data;

          if (!node || isPendingFolder(node)) return null;

          if (isFolderNode(node)) {
            return (
              <FolderActionsCell
                canManage={canManageFolder(node)}
                onMove={
                  canMoveFolder(node)
                    ? () => onOpenMoveDialog([node])
                    : undefined
                }
                onRename={() => onStartRename(node.id)}
                onDelete={() => onOpenDeleteFolder(node)}
                onShare={
                  canShareFolder(node) ? () => onShareFolder(node) : undefined
                }
                className="w-full"
              />
            );
          }

          return (
            <ActionsCell
              data={node}
              onOpenDeleteDialog={onOpenDeleteDialog}
              onUnselectTableRows={onUnselectTableRows}
              onOpenMoveDialog={() => onOpenMoveDialog([node])}
              className="w-full"
            />
          );
        },
        cellClass: '!p-0',
      },
    ],
    [
      onOpenMoveDialog,
      onOpenFolder,
      onCommitDraft,
      onCancelDraft,
      validateName,
      onStartRename,
      renamingFolderId,
      openingFolderId,
      onCommitRename,
      onCancelRename,
      onOpenDeleteDialog,
      onOpenDeleteFolder,
      onUnselectTableRows,
      canManageFolder,
      canMoveFolder,
      canShareFolder,
      onShareFolder,
      isBucketRoot,
      t,
    ],
  );

  const rowSelection: RowSelectionOptions<FileSystemNode> = useMemo(() => {
    return {
      mode: 'multiRow',
      hideDisabledCheckboxes: true,
      // Unprocessed files stay selectable so they can be deleted in bulk; the
      // toolbar then offers nothing else.
      isRowSelectable: ({ data }) => !isPendingFolder(data),
    };
  }, []);

  const onSelectionChanged = useCallback(
    (event: SelectionChangedEvent<FileSystemNode>) => {
      const selectedData = event.api
        .getSelectedNodes()
        .map((node) => node.data)
        .filter((data): data is FileSystemNode => Boolean(data));

      onSelectNodes(selectedData);
    },
    [onSelectNodes],
  );

  const openingFolderIdRef = useRef(openingFolderId);

  useEffect(() => {
    openingFolderIdRef.current = openingFolderId;
    gridRef.current?.api?.redrawRows();
  }, [gridRef, openingFolderId]);

  const rowClassRules = useMemo(
    () => ({
      'opacity-40': ({ data }: RowClassParams<FileSystemNode>) =>
        Boolean(openingFolderIdRef.current) &&
        data?.id !== openingFolderIdRef.current,
      // A folder not on the server yet, or a file the server has not processed.
      'opacity-60': ({ data }: RowClassParams<FileSystemNode>) =>
        isPendingFolder(data) || isUnprocessedFile(data),
    }),
    [],
  );

  return (
    <div
      className={cn(
        'min-h-0 w-full flex-1',
        openingFolderId && 'pointer-events-none',
      )}
      data-testid="file-system-table"
      aria-busy={Boolean(openingFolderId)}
    >
      <AgGridReact
        ref={gridRef}
        rowSelection={rowSelection}
        rowClassRules={rowClassRules}
        rowData={tableData}
        loading={isLoading}
        pinnedTopRowData={isDrafting ? FOLDER_DRAFT_ROWS : undefined}
        columnDefs={columnDefs}
        getRowId={(params) =>
          params.rowPinned ? FOLDER_DRAFT_ROW_ID : getNodeRowId(params.data)
        }
        rowModelType={'clientSide'}
        loadThemeGoogleFonts
        paginationAutoPageSize
        pagination
        onSelectionChanged={onSelectionChanged}
        suppressMovableColumns
        suppressContextMenu
        components={{
          agNoRowsOverlay: KnowledgeBaseNoRowOverlay,
        }}
        noRowsOverlayComponentParams={{ hidden: isDrafting }}
      />
    </div>
  );
};

export { FileSystemTable };
